import { FC, memo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Copy, Mail, Check } from 'lucide-react';

import { useScreenSizes } from '@hooks/useScreenSizes';
import { useAlert } from '@providers/AlertProvider';
import { AlertType, MOBILE_SKEW_ANGLE, SKEW_ANGLE } from '@utils/constants';
import {
  shareContent,
  openSocialShare,
  isWebShareSupported,
  SharePlatform,
} from '@utils/share';
import { DefaultText } from '@components/text';
import Modal from '@components/modal';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import SkewedButton from '@components/buttons/SkewedButton';
import cardImage from '@assets/images/card.png';
import xIcon from '@assets/icons/x.png';
import facebookIcon from '@assets/icons/facebook.png';
import whatsappIcon from '@assets/icons/whatsapp.png';
import theme from 'theme';

export type ShareContentType = 'show' | 'profile';

export type ShareData = {
  id: string;
  title: string;
  description?: string;
  image?: string | null;
  url: string;
  type: ShareContentType;
};

type Props = {
  isShown: boolean;
  onClose: () => void;
  shareData: ShareData;
};

const Wrapper = styled.div`
  flex: 1;
  width: 90%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: skewX(${SKEW_ANGLE}deg);
  padding-inline: 8px;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    transform: skewX(${MOBILE_SKEW_ANGLE}deg);
  }
`;

const SharePreview = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
`;

const PreviewImage = styled.div<{ $imageUrl?: string }>`
  width: 50%;
  aspect-ratio: 2 / 1;
  border-radius: 8px;
  background-image: ${({ $imageUrl }) =>
    $imageUrl ? `url(${$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  background-color: ${({ theme, $imageUrl }) =>
    $imageUrl ? 'transparent' : theme.colors.grey[700]};
  flex-shrink: 0;
`;

const PreviewContent = styled.div<{ $isCentered?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  ${({ $isCentered }) =>
    $isCentered &&
    `
    align-items: center;
  `}
`;

const PreviewTitle = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.bold};
  color: ${({ theme }) => theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShareOptions = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 12px;
`;

const ShareOption = styled(TouchableOpacity)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 12px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[700]};
  }
`;

const ShareIcon = styled.div<{ $color?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color, theme }) => $color || theme.colors.white};
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const InvertedIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: invert(100%);
`;

const CopyLinkSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const LinkInput = styled.input`
  flex: 1;
  height: 40px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const CopyButton = styled(SkewedButton)<{ $copied?: boolean }>`
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background-color: ${({ $copied, theme }) =>
    $copied ? theme.colors.success : theme.colors.primary};
  transition: all 0.2s ease;
  gap: 8px;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 200px;
    width: 100%;
  }
`;

const CopyLinkText = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.bold};
`;

const ShareModal: FC<Props> = ({ isShown, onClose, shareData }) => {
  const [copied, setCopied] = useState<boolean>(false);
  const { showAlert } = useAlert();
  const { isMobile } = useScreenSizes();

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy link:', error);
      showAlert(AlertType.Error, 'Failed to copy link');
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareData.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
    }
  }, [shareData.url, showAlert]);

  const handleSocialShare = useCallback(
    (platform: SharePlatform) => {
      const { title, url, description } = shareData;

      if (platform === 'email' && isWebShareSupported() && isMobile) {
        shareContent({
          title,
          text: description,
          url,
        });
        return;
      }

      openSocialShare({
        platform,
        title,
        url,
        description,
      });
    },
    [shareData, isMobile],
  );

  const getShareTitle = () => {
    switch (shareData.type) {
      case 'show':
        return 'Share Show';
      case 'profile':
        return 'Share Profile';
      default:
        return 'Share';
    }
  };

  if (!isShown) {
    return null;
  }

  return (
    <Modal
      isShown={isShown}
      title={getShareTitle()}
      onClose={onClose}
      isActionable={false}
    >
      <Wrapper>
        <SharePreview>
          {shareData.type === 'show' && (
            <PreviewImage $imageUrl={shareData.image || cardImage} />
          )}

          <PreviewContent $isCentered={shareData.type === 'profile'}>
            <PreviewTitle>{shareData.title}</PreviewTitle>
          </PreviewContent>
        </SharePreview>

        <ShareOptions>
          <ShareOption onClick={() => handleSocialShare('x')}>
            <InvertedIcon src={xIcon} />
          </ShareOption>

          <ShareOption onClick={() => handleSocialShare('facebook')}>
            <Icon src={facebookIcon} />
          </ShareOption>

          <ShareOption onClick={() => handleSocialShare('whatsapp')}>
            <Icon src={whatsappIcon} />
          </ShareOption>

          <ShareOption onClick={() => handleSocialShare('email')}>
            <ShareIcon>
              <Mail size={24} />
            </ShareIcon>
          </ShareOption>
        </ShareOptions>

        <CopyLinkSection>
          <LinkInput
            value={shareData.url}
            readOnly
            onClick={(e) => e.currentTarget.select()}
          />

          <CopyButton $copied={copied} onClick={handleCopyLink}>
            {copied ? (
              <Check size={16} color={theme.colors.white} />
            ) : (
              <Copy size={16} color={theme.colors.white} />
            )}

            <CopyLinkText>Copy Link</CopyLinkText>
          </CopyButton>
        </CopyLinkSection>
      </Wrapper>
    </Modal>
  );
};

export default memo(ShareModal);
